import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import LegalLayout from '../components/LegalLayout';

const PROVIDER = {
  name: 'HR Aristu, SLU',
  cif: 'B71445308',
  address: 'Calle San Nicolás 68-70, 6º, 31001 Pamplona (Navarra), España',
  email: 'soporte@hraristu.com'
};

const SECTIONS_ES = [
  {
    heading: '1. Objeto y partes',
    body: [
      `Este Acuerdo de Encargado de Tratamiento ("DPA") regula el tratamiento de datos personales que ${PROVIDER.name} (el "Encargado") realiza por cuenta de la empresa que contrata Regulisto (el "Responsable", es decir, tú) al gestionar la jornada laboral de sus propios trabajadores. Este DPA queda incorporado por referencia y forma parte integrante de los Términos de Servicio (/terms): al aceptar los Términos de Servicio en el registro, aceptas también este documento, conforme exige el artículo 28 del RGPD para cualquier relación responsable-encargado.`
    ]
  },
  {
    heading: '2. Duración',
    body: [
      'Este DPA está vigente mientras lo esté el contrato de prestación del Servicio (los Términos de Servicio), y se extiende durante el plazo de conservación de datos que aplique tras la baja, conforme a la cláusula 11.'
    ]
  },
  {
    heading: '3. Naturaleza, finalidad y duración del tratamiento',
    body: [
      'El Encargado trata los datos únicamente para prestar las funcionalidades del Servicio contratadas por el Responsable: registro de la jornada laboral, gestión de turnos, vacaciones, ausencias e incidencias, y generación de informes. El tratamiento dura lo que dure el contrato.'
    ]
  },
  {
    heading: '4. Categorías de interesados y de datos',
    body: [
      {
        list: [
          'Interesados: trabajadores del Responsable dados de alta en el Servicio (y, en su caso, personas candidatas o exempleados cuyos datos se conserven por obligación legal).',
          'Datos tratados: nombre, email, DNI/NIE (opcional), fecha de nacimiento, teléfono, dirección (opcionales); registros de fichaje (fecha, hora, tipo); geolocalización del fichaje si el Responsable activa esa función; PIN de fichaje; asignaciones de turno, vacaciones, ausencias e incidencias; y, si el Responsable las sube, fotografías de perfil.',
          'No se tratan de forma deliberada categorías especiales de datos (art. 9 RGPD); el Responsable se compromete a no introducir ese tipo de información en el Servicio salvo obligación legal expresa.'
        ]
      }
    ]
  },
  {
    heading: '5. Obligaciones del Encargado',
    body: [
      {
        list: [
          'Tratar los datos únicamente siguiendo las instrucciones documentadas del Responsable (que son, en esencia, la configuración que el propio Responsable define dentro del Servicio: qué ubicaciones, qué campos, qué informes).',
          'Garantizar que el personal autorizado a tratar los datos se compromete a la confidencialidad.',
          'Adoptar las medidas de seguridad descritas en la cláusula 7.',
          'No comunicar los datos a terceros salvo a los subencargados autorizados (cláusula 6) o por obligación legal.',
          'Asistir al Responsable, dentro de lo razonable, para que pueda responder a solicitudes de ejercicio de derechos de sus trabajadores y cumplir sus obligaciones de seguridad, notificación de violaciones y evaluaciones de impacto, en lo que afecte al Servicio.',
          'Suprimir o devolver los datos al finalizar la prestación del Servicio, conforme a la cláusula 11.',
          'Poner a disposición del Responsable la información necesaria para demostrar el cumplimiento de estas obligaciones.'
        ]
      }
    ]
  },
  {
    heading: '6. Subencargados autorizados',
    body: [
      `El Responsable autoriza con carácter general a ${PROVIDER.name} a recurrir a los siguientes subencargados, necesarios para prestar el Servicio:`,
      {
        list: [
          'Stripe, Inc. — procesamiento de pagos y facturación de la cuenta del Responsable (no trata datos de los trabajadores del Responsable, solo los del propio Responsable como cliente).',
          'Proveedor de alojamiento (infraestructura VPS/Coolify) — hospedaje de la aplicación y la base de datos donde residen los datos de fichaje.',
          'Proveedor de correo SMTP — envío de emails transaccionales del Servicio (confirmaciones, notificaciones).'
        ]
      },
      `${PROVIDER.name} informará al Responsable de cualquier cambio previsto en esta lista con antelación razonable, dando la oportunidad de oponerse por motivos justificados. Si alguno de estos subencargados está ubicado fuera del Espacio Económico Europeo, la transferencia se ampara en las Cláusulas Contractuales Tipo de la Comisión Europea u otra garantía adecuada conforme al Capítulo V del RGPD.`
    ]
  },
  {
    heading: '7. Medidas de seguridad',
    body: [
      {
        list: [
          'Contraseñas almacenadas con hash bcrypt, nunca en texto plano.',
          'Comunicaciones cifradas mediante HTTPS/TLS.',
          'Sesiones basadas en tokens JWT con expiración y revocación en el cierre de sesión.',
          'Aislamiento estricto de datos entre organizaciones clientes (arquitectura multi-tenant): un Responsable nunca puede ver datos de otro.',
          'Hash de verificación y firma digital en cada registro de fichaje para garantizar su integridad frente a manipulaciones.',
          'Control de acceso basado en roles dentro del Servicio (administrador, supervisor, trabajador, inspector).'
        ]
      }
    ]
  },
  {
    heading: '8. Notificación de violaciones de seguridad',
    body: [
      `${PROVIDER.name} notificará al Responsable sin dilación indebida cualquier violación de la seguridad de los datos personales de la que tenga conocimiento, con la información disponible para que el Responsable pueda, si procede, notificarlo a la autoridad de control y a los interesados conforme a los artículos 33 y 34 del RGPD.`
    ]
  },
  {
    heading: '9. Auditorías',
    body: [
      'El Responsable puede solicitar información razonable sobre las medidas de seguridad aplicadas para verificar el cumplimiento de este DPA. Por el volumen y naturaleza del Servicio, esto se atiende mediante cuestionarios de seguridad o documentación equivalente; una auditoría presencial se acordará caso por caso.'
    ]
  },
  {
    heading: '10. Devolución o supresión de datos al finalizar',
    body: [
      'Al finalizar la relación contractual, el Responsable puede exportar sus datos en los formatos disponibles en el Servicio (PDF, Excel, CSV). Pasado el plazo de conservación razonable tras la baja (ver Política de Privacidad, §6), los datos se suprimen o anonimizan, salvo que una norma imponga un plazo de conservación superior — por ejemplo, la normativa laboral española exige conservar los registros de jornada durante 4 años, plazo que se respeta con independencia de la baja del Servicio.'
    ]
  },
  {
    heading: '11. Responsabilidad y legislación aplicable',
    body: [
      'La responsabilidad de cada parte por el incumplimiento de este DPA se rige por lo establecido en los Términos de Servicio y por el RGPD y la LOPDGDD. Este DPA se rige por la legislación española, con sometimiento a los Juzgados y Tribunales de Pamplona (Navarra), en los mismos términos que los Términos de Servicio.'
    ]
  }
];

const SECTIONS_EN = [
  {
    heading: '1. Purpose and parties',
    body: [
      `This Data Processing Agreement ("DPA") governs the personal data processing that ${PROVIDER.name} (the "Processor") carries out on behalf of the company that subscribes to Regulisto (the "Controller", i.e. you) when managing its own employees' working time. This DPA is incorporated by reference into, and forms an integral part of, the Terms of Service (/terms): by accepting the Terms of Service at signup, you also accept this document, as required by Article 28 GDPR for any controller-processor relationship.`
    ]
  },
  {
    heading: '2. Term',
    body: [
      'This DPA remains in effect for as long as the Service agreement (the Terms of Service) does, and extends through the data retention period that applies after termination, per clause 11.'
    ]
  },
  {
    heading: '3. Nature, purpose and duration of processing',
    body: [
      'The Processor processes data solely to provide the Service features the Controller has subscribed to: time tracking, shift, vacation, absence and incident management, and report generation. Processing lasts for the duration of the contract.'
    ]
  },
  {
    heading: '4. Categories of data subjects and data',
    body: [
      {
        list: [
          'Data subjects: the Controller\'s employees registered in the Service (and, where applicable, candidates or former employees whose data is retained under a legal obligation).',
          'Data processed: name, email, national ID (optional), date of birth, phone, address (optional); clock-in records (date, time, type); clock-in geolocation if the Controller enables that feature; clock-in PIN; shift assignments, vacations, absences and incidents; and, if uploaded by the Controller, profile photos.',
          'No special categories of data (Art. 9 GDPR) are deliberately processed; the Controller agrees not to enter such data into the Service unless expressly required by law.'
        ]
      }
    ]
  },
  {
    heading: '5. Processor obligations',
    body: [
      {
        list: [
          'Process data only on the Controller\'s documented instructions (in practice, the configuration the Controller itself sets within the Service: which locations, which fields, which reports).',
          'Ensure personnel authorized to process the data are bound by confidentiality.',
          'Implement the security measures described in clause 7.',
          'Not disclose the data to third parties other than the authorized sub-processors (clause 6) or as required by law.',
          'Reasonably assist the Controller in responding to its employees\' rights requests and meeting its security, breach-notification and impact-assessment obligations, to the extent this concerns the Service.',
          'Delete or return the data when the Service ends, per clause 11.',
          'Make available to the Controller the information necessary to demonstrate compliance with these obligations.'
        ]
      }
    ]
  },
  {
    heading: '6. Authorized sub-processors',
    body: [
      `The Controller gives general authorization for ${PROVIDER.name} to use the following sub-processors, necessary to provide the Service:`,
      {
        list: [
          'Stripe, Inc. — payment processing and billing for the Controller\'s own account (does not process the Controller\'s employees\' data, only the Controller\'s own data as a customer).',
          'Hosting provider (VPS/Coolify infrastructure) — hosting the application and the database where clock-in data resides.',
          'SMTP email provider — sending the Service\'s transactional emails (confirmations, notifications).'
        ]
      },
      `${PROVIDER.name} will inform the Controller of any intended change to this list with reasonable notice, giving the opportunity to object on justified grounds. If any of these sub-processors is located outside the European Economic Area, the transfer is covered by the European Commission's Standard Contractual Clauses or another appropriate safeguard under Chapter V GDPR.`
    ]
  },
  {
    heading: '7. Security measures',
    body: [
      {
        list: [
          'Passwords stored with bcrypt hashing, never in plain text.',
          'Encrypted communications via HTTPS/TLS.',
          'JWT-based sessions with expiration and revocation on logout.',
          'Strict data isolation between customer organizations (multi-tenant architecture): one Controller can never see another\'s data.',
          'Verification hash and digital signature on every clock-in record to guarantee integrity against tampering.',
          'Role-based access control within the Service (admin, supervisor, worker, inspector).'
        ]
      }
    ]
  },
  {
    heading: '8. Data breach notification',
    body: [
      `${PROVIDER.name} will notify the Controller without undue delay of any personal data breach it becomes aware of, with the information available so the Controller can, if applicable, notify the supervisory authority and data subjects under Articles 33 and 34 GDPR.`
    ]
  },
  {
    heading: '9. Audits',
    body: [
      'The Controller may request reasonable information about the security measures applied to verify compliance with this DPA. Given the scale and nature of the Service, this is handled through security questionnaires or equivalent documentation; an on-site audit would be arranged on a case-by-case basis.'
    ]
  },
  {
    heading: '10. Return or deletion of data on termination',
    body: [
      'When the contractual relationship ends, the Controller can export its data in the formats available in the Service (PDF, Excel, CSV). After the reasonable retention period following termination (see Privacy Policy, §6), data is deleted or anonymized, unless a longer retention period is required by law — for example, Spanish labor law requires keeping time-tracking records for 4 years, a period that is honored regardless of when the Service subscription ends.'
    ]
  },
  {
    heading: '11. Liability and governing law',
    body: [
      'Each party\'s liability for breach of this DPA is governed by the Terms of Service and by GDPR and Spanish data protection law (LOPDGDD). This DPA is governed by Spanish law, with the Courts of Pamplona (Navarra) having jurisdiction, on the same terms as the Terms of Service.'
    ]
  }
];

const DataProcessingAgreement = () => {
  const { language } = useLanguage();
  const isEs = language === 'es';

  return (
    <LegalLayout
      title={isEs ? 'Acuerdo de Encargado de Tratamiento (DPA)' : 'Data Processing Agreement (DPA)'}
      effectiveDate={isEs ? '23 de agosto de 2026' : 'August 23, 2026'}
      sections={isEs ? SECTIONS_ES : SECTIONS_EN}
    />
  );
};

export default DataProcessingAgreement;
